/**
 * Query-driven panels: a chart, a stat and a label-keyed table that each own
 * one PromQL query and one time range.
 *
 * Why the query is a PROP and not a callback: a panel that took a fetch
 * function would re-fetch on every parent render, because a new closure is a
 * new dependency. A query string and a `TimeRangeValue` held in the parent's
 * state are stable, so the effect runs when the question or the window actually
 * changes — which is also what makes a 12-panel tab load 12 requests instead of
 * 12 per keystroke.
 *
 * Every query is written WITHOUT a tenant matcher; the API proxy injects the
 * tenant and the node allow-list (design 19 §6.3).
 */

import { useCallback, useEffect, useState } from 'react';
import { MetricsChart } from './MetricsChart';
import accessObservabilityService, { type ChartPoint } from '../../services/accessObservability.service';
import { ACTION_COLORS, dynamicLines } from '../../utils/obsChart';
import { resolveTimeRange, type TimeRangeValue } from '../../utils/timeRange';

interface QueryChartProps {
  title: string;
  query: string;
  range: TimeRangeValue;
  /** Labels that name a series, e.g. ['app'] or ['iface','direction']. */
  labelKeys?: string[];
  unit?: string;
  /** Colour by action name (allow green / deny red / error amber). */
  colorByAction?: boolean;
  testId?: string;
}

export const QueryChart = ({
  title,
  query,
  range,
  labelKeys,
  unit = '',
  colorByAction = false,
  testId,
}: QueryChartProps) => {
  const [data, setData] = useState<ChartPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const keys = labelKeys?.join(',') ?? '';

  const load = useCallback(async () => {
    const { start, end, step } = resolveTimeRange(range);
    setLoading(true);
    try {
      const wanted = keys ? keys.split(',') : undefined;
      setData(
        await accessObservabilityService.series(query, start, end, step, (m) => {
          if (wanted?.length) {
            const parts = wanted.map((k) => m[k]).filter(Boolean);
            if (parts.length) return parts.join(' · ');
          }
          const rest = Object.entries(m).filter(
            ([k]) => k !== '__name__' && k !== 'node_kind' && k !== 'gateway_id' && k !== 'connector_id' && k !== 'tenant',
          );
          return rest.length ? rest.map(([, v]) => v).join(' · ') : (m.__name__ ?? 'value');
        }),
      );
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
    // `keys` is the string form of labelKeys precisely so an inline array prop
    // cannot restart this effect on every render.
  }, [query, range, keys]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div data-testid={testId}>
      <MetricsChart
        title={title}
        data={data}
        loading={loading}
        unit={unit}
        lines={dynamicLines(data, colorByAction ? (k) => ACTION_COLORS[k] : undefined)}
      />
    </div>
  );
};

interface QueryStatProps {
  title: string;
  query: string;
  /**
   * Rendered from the scalar of a query that ANSWERED. `null` here means the
   * answer was empty ("nothing matched"), which for a count or a sum is a
   * truthful 0. A query that FAILED never reaches `format`: it renders "—" with
   * an error state (VSNP-1436), because "the metrics store did not answer" must
   * not read as "zero activity".
   */
  format?: (v: number | null) => string;
  hint?: string;
  testId?: string;
  /**
   * Told after every load whether the query failed, keyed by `testId`, so a
   * page can say once, at the top, that its live numbers are unavailable.
   * Must be a stable callback (useCallback): it is a dependency of the fetch.
   */
  onStatus?: (testId: string, failed: boolean) => void;
}

export const QueryStat = ({ title, query, format, hint, testId, onStatus }: QueryStatProps) => {
  const [value, setValue] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    let ok = false;
    try {
      setValue(await accessObservabilityService.instantScalar(query));
      ok = true;
    } catch {
      setValue(null);
    } finally {
      setFailed(!ok);
      setLoading(false);
      onStatus?.(testId ?? title, !ok);
    }
  }, [query, onStatus, testId, title]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = failed
    ? '—'
    : format
      ? format(value)
      : value === null ? '—' : String(Math.round(value * 100) / 100);
  return (
    <div className="card" data-testid={testId}>
      <p className="text-sm font-medium text-gray-600">{title}</p>
      <p
        className={`mt-1 text-2xl font-bold ${loading ? 'text-gray-300' : failed ? 'text-gray-400' : 'text-gray-900'}`}
        data-stat={testId}
        data-state={loading ? 'loading' : failed ? 'error' : 'ok'}
        title={failed ? 'The metrics store did not answer — this is not the same as zero' : undefined}
      >
        {loading ? '…' : shown}
      </p>
      {failed && !loading ? (
        <p className="mt-0.5 text-xs text-warning-700" data-testid={testId ? `${testId}-error` : undefined}>
          metrics unavailable
        </p>
      ) : (
        hint && <p className="mt-0.5 text-xs text-gray-400">{hint}</p>
      )}
    </div>
  );
};

interface QueryTableProps {
  title: string;
  query: string;
  /** The label whose values become rows. */
  labelKey: string;
  valueLabel: string;
  format?: (v: number) => string;
  /** Rows whose value is 1 are highlighted (used for `*_unserved`). */
  alertOnOne?: boolean;
  emptyMessage?: string;
  testId?: string;
}

/** An instant query rendered as a label→value list (connector coverage, top-N). */
export const QueryTable = ({
  title,
  query,
  labelKey,
  valueLabel,
  format,
  alertOnOne = false,
  emptyMessage = 'No data',
  testId,
}: QueryTableProps) => {
  const [rows, setRows] = useState<[string, number][]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const byLabel = await accessObservabilityService.instantBy(query, labelKey);
      setRows(Object.entries(byLabel).sort((a, b) => b[1] - a[1]));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [query, labelKey]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="card" data-testid={testId}>
      <h3 className="mb-2 text-sm font-semibold text-gray-900">{title}</h3>
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400">{emptyMessage}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="pb-1">{labelKey}</th>
              <th className="pb-1 text-right">{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, value]) => (
              <tr key={key} className={alertOnOne && value === 1 ? 'text-danger-700' : 'text-gray-700'}>
                <td className="py-0.5 pr-2 font-mono text-xs">{key || '—'}</td>
                <td className="py-0.5 text-right">{format ? format(value) : value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
